import { format } from 'date-fns';

import {
  TreatmentRecordRequestChannelType,
  TreatmentRecordRequestStatus,
} from '@/services/api/generated/treatmentRecordRequestsService';

/**
 * Get the next scheduled voice AI call date from backend
 * @param nextScheduledCall - The scheduled next call date from backend metadata
 * @returns The next scheduled call date, or null if not scheduled
 */
export function getNextCallDate(
  nextScheduledCall?: string | null,
): Date | null {
  // Simply return the backend-provided scheduled call date
  // Backend is the source of truth for all scheduling logic
  if (nextScheduledCall) {
    return new Date(nextScheduledCall);
  }

  return null;
}

/**
 * Convert interval days to a human-friendly label
 * @param days - Number of days
 * @returns Friendly label for the interval
 */
export function getIntervalLabel(days: number): string {
  switch (days) {
    case 1:
      return 'Daily';
    case 2:
      return 'Every 2 days';
    case 3:
      return 'Every 3 days';
    case 5:
      return 'Every 5 days';
    case 7:
      return 'Weekly';
    default:
      // For any invalid values, show the number of days
      return `Every ${days} day${days === 1 ? '' : 's'}`;
  }
}

/**
 * Format the next call date for display
 * @param nextCallDate - The date of the next call
 * @returns Formatted string for display
 */
export function formatNextCallDate(nextCallDate: Date | null): string {
  if (!nextCallDate) {
    return 'Not scheduled';
  }

  const now = new Date();
  const diffInHours =
    (nextCallDate.getTime() - now.getTime()) / (1000 * 60 * 60);

  if (diffInHours < 0) {
    return 'Overdue';
  }
  if (diffInHours < 24) {
    return 'Today';
  }
  if (diffInHours < 48) {
    return 'Tomorrow';
  }
  return format(nextCallDate, 'MMM d, yyyy');
}

/**
 * Get the next call status with appropriate messaging
 * @param nextCallDate - The calculated next call date
 * @param channelType - The request channel type
 * @param status - The request status
 * @returns Status message and whether a call is scheduled
 */
export function getNextCallStatus(
  nextCallDate: Date | null,
  channelType?: TreatmentRecordRequestChannelType | null,
  status?: TreatmentRecordRequestStatus | null,
): { message: string; isScheduled: boolean } {
  if (channelType === 'portal') {
    return { message: 'Not applicable - Portal requests', isScheduled: false };
  }

  // For pending status, always show "Will begin after submission"
  if (status === 'pending') {
    return { message: 'Will begin after submission', isScheduled: false };
  }

  if (!nextCallDate) {
    return { message: 'No calls scheduled (14+ days)', isScheduled: false };
  }

  const now = new Date();
  if (nextCallDate < now) {
    return { message: 'Call in progress or overdue', isScheduled: true };
  }

  return { message: formatNextCallDate(nextCallDate), isScheduled: true };
}

/**
 * Validate if the interval is within acceptable range
 * @param days - Number of days to validate
 * @returns True if valid (1-7 days, max weekly)
 */
export function isValidInterval(days: number): boolean {
  return Number.isInteger(days) && days >= 1 && days <= 7;
}

/**
 * Get the badge color for different interval frequencies
 * @param days - Number of days in the interval
 * @returns Tailwind color class for the badge
 */
export function getIntervalBadgeColor(days: number): string {
  if (days === 1) {
    return 'bg-red-100 text-red-800'; // Daily - urgent
  }
  if (days <= 3) {
    return 'bg-orange-100 text-orange-800'; // Frequent
  }
  if (days <= 7) {
    return 'bg-yellow-100 text-yellow-800'; // Weekly
  }
  return 'bg-green-100 text-green-800'; // Less frequent
}

/**
 * Check if Voice AI follow-ups are applicable for a request
 * @param channelType - The request channel type
 * @param status - The request status
 * @returns True if Voice AI follow-ups apply
 */
export function isVoiceAIApplicable(
  channelType?: TreatmentRecordRequestChannelType | null,
  status?: TreatmentRecordRequestStatus | null,
): boolean {
  // Voice AI only applies to non-portal channels
  if (!channelType || channelType === 'portal') {
    return false;
  }

  // Voice AI only starts after request is submitted
  const activeStatuses: TreatmentRecordRequestStatus[] = [
    'request_submitted',
    'request_received',
    'awaiting_invoice',
    'pending_payment',
    'records_pending',
  ];

  return status ? activeStatuses.includes(status) : false;
}

/**
 * Check if Voice AI follow-ups will be applicable (pending state)
 * @param channelType - The request channel type
 * @returns True if Voice AI will apply after submission
 */
export function willVoiceAIBeApplicable(
  channelType?: TreatmentRecordRequestChannelType | null,
): boolean {
  return channelType ? channelType !== 'portal' : false;
}

/**
 * Get appropriate Voice AI status message
 * @param channelType - The request channel type
 * @param status - The request status
 * @returns User-friendly status message
 */
export function getVoiceAIStatusMessage(
  channelType?: TreatmentRecordRequestChannelType | null,
  status?: TreatmentRecordRequestStatus | null,
): string {
  // Portal requests never have Voice AI
  if (channelType === 'portal') {
    return 'Not applicable - Portal requests are tracked automatically';
  }

  // Check if status is pending
  if (status === 'pending') {
    return 'Will begin after request submission';
  }

  // Check if Voice AI is currently active
  if (isVoiceAIApplicable(channelType, status)) {
    return 'Active - Automated follow-up calls enabled';
  }

  // Completed, cancelled, or auto-retried statuses
  if (
    status === 'records_delivered' ||
    status === 'records_ingested' ||
    status === 'cancelled' ||
    status === 'rejected' ||
    status === 'auto_retried'
  ) {
    return 'Completed - No further calls needed';
  }

  return 'Not applicable';
}

/**
 * Get badge color for Voice AI status
 * @param channelType - The request channel type
 * @param status - The request status
 * @returns Tailwind color classes for badge
 */
export function getVoiceAIStatusBadgeColor(
  channelType?: TreatmentRecordRequestChannelType | null,
  status?: TreatmentRecordRequestStatus | null,
): string {
  if (channelType === 'portal') {
    return 'bg-gray-100 text-gray-600'; // Not applicable
  }

  if (status === 'pending') {
    return 'bg-blue-100 text-blue-800'; // Scheduled
  }

  if (isVoiceAIApplicable(channelType, status)) {
    return 'bg-green-100 text-green-800'; // Active
  }

  return 'bg-gray-100 text-gray-600'; // Inactive
}
